'use client'

import { useState } from 'react'
import { MoreHorizontal, Edit, Trash2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMutation } from '@apollo/client'
import { toast } from 'sonner'
import { CustomerSubAccount } from '../types'
import {
  DELETE_ONE_CUSTOMER_SUB_ACCOUNT,
  FIND_MANY_CUSTOMER_SUB_ACCOUNT,
} from '../customerSubAccountQueries'
import { useConfirmation } from '@/context/ConfirmationContext'

interface CustomerSubAccountActionsProps {
  customerSubAccount: CustomerSubAccount
  onEdit: (customerSubAccount: CustomerSubAccount) => void
  onAddUser?: (customerSubAccount: CustomerSubAccount) => void
}

export function CustomerSubAccountActions({
  customerSubAccount,
  onEdit,
  onAddUser,
}: CustomerSubAccountActionsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { showConfirmation } = useConfirmation()

  const [deleteCustomerSubAccount, { loading: deleteLoading }] = useMutation(
    DELETE_ONE_CUSTOMER_SUB_ACCOUNT,
    {
      onError: e => {
        console.error('Error deleting location:', e)
        toast.error('Failed to delete location')
      },
      onCompleted: () => {
        toast.success('Location deleted successfully')
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_SUB_ACCOUNT,
          variables: { customerId: customerSubAccount.customerId },
        },
      ],
    }
  )

  const handleEdit = () => {
    onEdit(customerSubAccount)
    setIsOpen(false)
  }

  const handleDelete = () => {
    showConfirmation({
      title: 'Delete Location',
      description: `Are you sure you want to delete "${
        customerSubAccount.name || 'this location'
      }"? This action cannot be undone.`,
      onConfirm: () => {
        deleteCustomerSubAccount({
          variables: { id: customerSubAccount.id },
        })
      },
    })
    setIsOpen(false)
  }

  const handleAddUser = () => {
    if (onAddUser) {
      onAddUser(customerSubAccount)
    }
    setIsOpen(false)
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onAddUser && (
          <DropdownMenuItem onClick={handleAddUser}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleEdit}>
          <Edit className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleDelete}
          disabled={deleteLoading}
          className="text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
